import { Height } from '@mui/icons-material';
import React, { useState, useRef } from 'react';

const termsPages = [
    {
        title: "Introduction",
        content: [
            "Welcome to our platform.",
            "By using this service, you agree to the outlined terms.",
            "Please read them carefully before proceeding.",
            "These terms constitute a legal agreement between you and us.",
            "Your use of our services is subject to compliance.",
            "Do not proceed if you do not accept these terms.",
            "We may update these terms periodically.",
            "Your continued usage implies your acceptance of the latest terms.",
            "You should print or save a copy for your records.",
            "If you are under 18, you must use our service under supervision.",
            "Our platform is intended for lawful and ethical usage only."
        ],
    },
    {
        title: "User Responsibilities",
        content: [
            "You must provide accurate registration details.",
            "Keep your account credentials secure.",
            "Refrain from illegal or abusive activities.",
            "Respect the rights and privacy of other users.",
            "Do not impersonate another individual or entity.",
            "Update your information regularly to keep it accurate.",
            "Immediately notify us of unauthorized access.",
            "Do not exploit bugs or vulnerabilities in the platform.",
            "Report suspicious behavior or misuse.",
            "You are responsible for all activities under your account.",
            "Abide by the community guidelines at all times."
        ],
    },
    {
        title: "Privacy Policy",
        content: [
            "We do not sell your personal information to third parties.",
            "Your data is stored securely and handled with confidentiality.",
            "We use cookies to improve user experience.",
            "We collect data to enhance performance and services.",
            "You can review and update your privacy settings.",
            "Third-party tools may access limited data for service delivery.",
            "We comply with applicable data protection laws.",
            "You have the right to access and delete your data.",
            "Analytics data is anonymized for research purposes.",
            "All sensitive data is encrypted in storage and transit.",
            "Review our complete privacy policy for full details."
        ],
    },
    {
        title: "Termination Clause",
        content: [
            "Violation of terms may result in immediate termination.",
            "We reserve the right to suspend accounts without prior notice.",
            "Terminated users may lose access to all stored data.",
            "Appeals for account reinstatement are evaluated case-by-case.",
            "Repeated violations may lead to permanent bans.",
            "Accounts with no activity for 12 months may be deactivated.",
            "We are not obligated to provide a reason for termination.",
            "Illegal activity may be reported to authorities.",
            "All user data is purged after termination unless required by law.",
            "Your subscription fees are non-refundable upon termination.",
            "Misuse of service tools is a ground for instant ban."
        ],
    },
    {
        title: "Governing Law",
        content: [
            "These terms are governed by Indian law.",
            "Any disputes shall be settled in the jurisdiction of our headquarters.",
            "You agree to comply with all applicable regulations.",
            "Local laws of your region may also apply.",
            "We are not responsible for compliance outside our jurisdiction.",
            "Legal disputes must be communicated in writing first.",
            "Failure to resolve disputes amicably may result in litigation.",
            "We are governed under the Information Technology Act, 2000.",
            "Jurisdiction is exclusively with courts in our legal district.",
            "We strive to comply with national and international standards.",
            "Disputes regarding IP or user rights will be prioritized legally."
        ],
    },
    {
        title: "Payment Terms",
        content: [
            "Users must pay all applicable fees on time.",
            "Late payments may result in account suspension.",
            "We use third-party services for secure transactions.",
            "You must ensure your payment method is valid.",
            "Refunds are subject to approval and policy.",
            "Subscription renewals occur automatically unless canceled.",
            "You will receive email notifications for payment activity.",
            "Chargebacks or disputes may result in account review.",
            "Taxes are included or added based on your location.",
            "Special discounts are non-transferable.",
            "Trial periods must be canceled before billing date to avoid charges."
        ],
    },
    {
        title: "License and Access",
        content: [
            "We grant limited, non-transferable access to use our services.",
            "You may not download or modify any part of the site.",
            "Any misuse will be considered a breach of terms.",
            "You are not permitted to reverse-engineer our platform.",
            "You must not copy or mirror our services without permission.",
            "Access is personal and not assignable to others.",
            "Our license does not include resale or commercial use.",
            "Unauthorized use will lead to access revocation.",
            "APIs and developer tools are governed by separate terms.",
            "We monitor usage patterns to prevent abuse.",
            "Misuse includes overloading, scraping, or spoofing traffic."
        ],
    },
    {
        title: "Account Termination",
        content: [
            "Accounts may be terminated due to policy violations.",
            "We may retain usage data even after termination.",
            "Termination does not waive your obligations.",
            "Outstanding payments are due even after termination.",
            "Violation of intellectual property rights leads to permanent ban.",
            "Repeated report-based warnings may trigger auto-lock.",
            "Deactivated accounts lose access to premium features.",
            "We offer no guarantee of reinstatement after termination.",
            "Any attempt to bypass bans is a serious violation.",
            "Admins reserve the right to block IP or device.",
            "Terminated users may be blacklisted permanently."
        ],
    },
    {
        title: "User Content",
        content: [
            "You are responsible for any content you share.",
            "Do not upload illegal or offensive material.",
            "We may remove inappropriate content at any time.",
            "You retain rights to your content unless otherwise stated.",
            "Content must not infringe third-party rights.",
            "You grant us a license to display your content.",
            "Spam or malicious files are prohibited.",
            "Do not post misleading, false, or harmful content.",
            "We do not endorse any user-generated content.",
            "Users may flag objectionable content for review.",
            "Violation of content policies may lead to suspension."
        ],
    },
    {
        title: "Changes to the Agreement",
        content: [
            "We reserve the right to update these terms.",
            "You are responsible for checking updates periodically.",
            "Continued use indicates acceptance of changes.",
            "Material changes will be communicated via email or platform.",
            "Previous versions will be archived for reference.",
            "Significant updates may require re-consent.",
            "Policy changes are effective immediately upon posting.",
            "We encourage reviewing terms before each usage.",
            "We may update specific sections without notice.",
            "Always refer to the latest version for compliance.",
            "Your feedback may be used for improving our policies."
        ],
    },
    {
        title: "Intellectual Property",
        content: [
            "All content and design elements are protected by copyright.",
            "Do not use our logos or trademarks without permission.",
            "Respect our intellectual property rights.",
            "All product names are trademarks of their respective holders.",
            "You may not reproduce, distribute, or display our content.",
            "Infringement will result in legal action.",
            "We investigate copyright claims promptly.",
            "Users may not claim ownership of platform-generated content.",
            "Open source components are governed by their own licenses.",
            "Licenses do not transfer platform ownership to users.",
            "You must notify us of any suspected infringement."
        ],
    },
    {
        title: "Third-Party Services",
        content: [
            "We are not liable for third-party service issues.",
            "Use of third-party features is at your own risk.",
            "Review their terms separately before use.",
            "We do not control third-party content or tools.",
            "Data shared with third parties is limited and consented.",
            "You agree to the third-party's privacy and refund policies.",
            "We may remove or replace third-party integrations anytime.",
            "You may be redirected to third-party platforms.",
            "We are not liable for losses from third-party links.",
            "Ensure secure use of third-party payment services.",
            "Third-party errors do not obligate platform compensation."
        ],
    },
    {
        title: "Communication Policy",
        content: [
            "We may contact you via email or phone.",
            "You can manage your communication preferences.",
            "All messages will be relevant and secure.",
            "We use your details to share product updates.",
            "You can opt-out of promotional messages anytime.",
            "Transactional messages cannot be opted-out.",
            "We ensure no spam or advertising from partners.",
            "Communication logs are stored for auditing purposes.",
            "We respect Do Not Disturb preferences when possible.",
            "Notifications may appear on platform dashboard too.",
            "We respond to support emails within 48 hours."
        ],
    },
    {
        title: "Disclaimer of Warranties",
        content: [
            "We provide services 'as is' without warranties.",
            "We do not guarantee uninterrupted service.",
            "You use the platform at your own risk.",
            "No warranty of merchantability or fitness is implied.",
            "We do not guarantee compatibility with all devices.",
            "We are not responsible for data loss or corruption.",
            "Downtime for maintenance may occur without notice.",
            "You acknowledge inherent risks in digital services.",
            "Performance may vary by region and connection quality.",
            "We disclaim liability for third-party service errors.",
            "No advice or information from us creates a warranty."
        ],
    },
    {
        title: "Final Acceptance",
        content: [
            "Please review all terms before proceeding.",
            "Check the box below to confirm your acceptance.",
            "Acceptance is required to continue using our platform.",
            "Once accepted, these terms are binding."
        ],
    },
];


const Termsandconditions = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const [accepted, setAccepted] = useState(false);
    const contentRef = useRef(null);
    // const [signatureImage, setSignatureImage] = useState(null);
    const [signatureImage, setSignatureImage] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(null);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const nextPage = () => {
        if (currentPage < termsPages.length - 1) {
            setCurrentPage(prev => {
                const newPage = prev + 1;
                setTimeout(scrollToTop, 100); // smooth scroll after state updates
                return newPage;
            });
        }
    };

    const prevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(prev => {
                const newPage = prev - 1;
                setTimeout(scrollToTop, 100); // smooth scroll after state updates
                return newPage;
            });
        }
    };


    const handleCheckboxChange = (e) => {
        setAccepted(e.target.checked);
    };

    const isLastPage = currentPage === termsPages.length - 1;

    return (
        <div style={styles.container} >
            <div style={styles.page} ref={contentRef}>
                <h2 style={styles.title}>{termsPages[currentPage].title}</h2>
                <ul style={styles.list}>
                    {termsPages[currentPage].content.map((point, index) => (
                        <li key={index} style={styles.listItem}>{point}</li>
                    ))}
                </ul>

                {isLastPage ? (
                    <>
                        <div style={styles.checkboxContainer}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={accepted}
                                    onChange={handleCheckboxChange}
                                    style={{ marginRight: '8px' }}
                                />
                                I accept the terms and conditions
                            </label>
                        </div>

                        <div style={styles.signatureContainer}>
                            <label style={styles.signatureUploadBox}>
                                {signaturePreview ? (
                                    <img
                                        src={signaturePreview}
                                        alt="Signature Preview"
                                        style={styles.signaturePreview}
                                    />
                                ) : (
                                    <span style={styles.uploadText}>Click to upload signature</span>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        setSignatureImage(file);
                                        if (file) {
                                            const imageUrl = URL.createObjectURL(file);
                                            setSignaturePreview(imageUrl);
                                        }
                                    }}
                                />
                            </label>
                        </div>



                        <div style={styles.buttons}>
                            <button
                                onClick={prevPage}
                                style={{ ...styles.button, ...styles.prev }}
                            >
                                ← Previous
                            </button>
                            <button
                                disabled={!accepted}
                                style={{ ...styles.button, ...styles.save }}
                                onClick={() => alert("✔️ Terms accepted and signature saved!")}
                            >
                                Save
                            </button>
                        </div>
                    </>
                ) : (
                    <div style={styles.buttons}>
                        <button
                            onClick={prevPage}
                            disabled={currentPage === 0}
                            style={{ ...styles.button, ...styles.prev }}
                        >
                            ← Previous
                        </button>
                        <button
                            onClick={nextPage}
                            style={{ ...styles.button, ...styles.next }}
                        >
                            Next →
                        </button>
                    </div>
                )}


            </div>

            {/* <div style={styles.buttons}>
                <button onClick={prevPage} disabled={currentPage === 0} style={{ ...styles.button, ...styles.prev }}>
                    ← Previous
                </button>
                <button
                    onClick={nextPage}
                    disabled={isLastPage ? !accepted : false}
                    style={{ ...styles.button, ...styles.next }}
                >
                    {isLastPage ? "Finish" : "Next →"}
                </button>
            </div> */}

            <p style={styles.pageNumber}>Page {currentPage + 1} of {termsPages.length}</p>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: "791px",
        Height: "700px",
        marginTop: "120px",
        margin: "50px auto",
        padding: "30px",
        border: "1px solid #ddd",
        borderRadius: "12px",
        fontFamily: "Segoe UI, sans-serif",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
    },
    page: {
        marginBottom: "30px",
    },
    title: {
        fontSize: "24px",
        marginBottom: "20px",
        color: "#333",
        fontWeight: "bold",
    },
    list: {
        paddingLeft: "20px",
    },
    listItem: {
        marginBottom: "12px",
        fontSize: "16px",
        color: "#444",
        lineHeight: "1.6",
    },
    checkboxContainer: {
        marginTop: "20px",
        fontSize: "16px",
        color: "#333",
    },
    buttons: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "20px",
    },
    button: {
        padding: "10px 20px",
        fontSize: "16px",
        borderRadius: "8px",
        border: "none",
        cursor: "pointer",
        transition: "background 0.3s, transform 0.2s",
    },
    prev: {
        backgroundColor: "#e0e0e0",
        color: "#333",
    },
    next: {
        backgroundColor: "rgb(177 92 214)",
        color: "#fff",
    },
    pageNumber: {
        textAlign: "center",
        marginTop: "20px",
        color: "#888",
        fontSize: "14px",
    },
    signatureContainer: {
        // textAlign: "center",
        marginTop: "30px",
    },
    signatureLabel: {
        fontSize: "16px",
        color: "#333",
        display: "inline-block",
        marginBottom: "20px",
    },
    signatureInput: {
        display: "block",
        margin: "10px auto",
    },
    saveButtonWrapper: {
        display: "flex",
        justifyContent: "center",
        marginTop: "20px",
    },
    save: {
        backgroundColor: "#28a745",
        color: "#fff",
        padding: "12px 32px",
        fontSize: "18px",
        borderRadius: "8px",
        border: "none",
        cursor: "pointer",
    },
    signaturePreview: {
        display: "block",
        marginTop: "10px",
        maxWidth: "250px",
        maxHeight: "100px",
        objectFit: "contain",
        border: "1px solid #ccc",
        borderRadius: "6px",
    },
    uploadText: {
        color: "#666",
        fontSize: "16px",  // increased font size
        fontWeight: "bold",  // made text bold
    },

};

export default Termsandconditions;
